import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Edge } from '@xyflow/react';
import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Trash2, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { useCanvasStore } from '@features/flows/state/canvas-store';
import { resolveEdgeSchemas } from '@features/flows/lib/autocast/edge-context';
import { flattenProperties } from '@features/flows/lib/autocast/schema-utils';
import CodeEditor from '@shared/ui/adapters/shadcn/widgets/CodeEditor';
import { cn } from '@shared/utils/cn';
import { TargetFieldCombobox } from './TargetFieldCombobox';

const normalizeLiquids = (input: Record<string, string>): Record<string, string> => {
  const normalized: Record<string, string> = {};
  for (const [to, code] of Object.entries(input)) {
    normalized[to] = String(code ?? '');
  }
  return normalized;
};

type Props = { edgeId: string };

type LiquidDraft = Record<string, string>;

type SaveTimers = Record<string, ReturnType<typeof setTimeout>>;

type SaveOptions = {
  persistEmpty?: boolean;
};

export default function LiquidGridPanel({ edgeId }: Props) {
  const edge = useCanvasStore(
    React.useCallback((state) => state.graph.edges.find((e) => e.id === edgeId), [edgeId]),
  ) as Edge | undefined;
  const setEdgeLiquid = useCanvasStore((state) => state.setEdgeLiquid);
  const runLLM = useCanvasStore((state) => state.llmAutocastEdge);
  const setEdgeInstructions = useCanvasStore((state) => state.setEdgeInstructions);
  const nodes = useCanvasStore((state) => state.graph.nodes);
  const templates = useCanvasStore((state) => state.templates);

  const mappingLiquid = useMemo(() => {
    const data = (edge?.data ?? {}) as Record<string, unknown>;
    return normalizeLiquids((data.mappingLiquid as Record<string, string> | undefined) ?? {});
  }, [edge]);
  const mappingLLMRunning = Boolean((edge?.data as any)?.mappingLLMRunning);
  const mappingLLMError = typeof (edge?.data as any)?.mappingLLMError === 'string'
    ? (edge!.data as any).mappingLLMError
    : null;
  const instructions = typeof (edge?.data as any)?.mappingInstructions === 'string'
    ? (edge!.data as any).mappingInstructions
    : '';

  const [drafts, setDrafts] = useState<LiquidDraft>(() => normalizeLiquids(mappingLiquid));
  const [editingField, setEditingField] = useState<string | null>(null);
  const [fieldNameDraft, setFieldNameDraft] = useState('');
  const [showInstructions, setShowInstructions] = useState(false);
  const [instructionsDraft, setInstructionsDraft] = useState(instructions);
  const saveTimers = useRef<SaveTimers>({});
  const [userAddedFields, setUserAddedFields] = useState<Set<string>>(new Set());

  useEffect(() => {
    setDrafts(normalizeLiquids(mappingLiquid));
  }, [mappingLiquid]);

  // Reset user-added fields tracking ONLY when edge changes
  useEffect(() => {
    setUserAddedFields(new Set());
  }, [edgeId]);

  useEffect(() => {
    setInstructionsDraft(instructions);
  }, [instructions]);

  useEffect(() => {
    return () => {
      Object.values(saveTimers.current).forEach(clearTimeout);
    };
  }, []);

  const scheduleSave = useCallback(
    (path: string, code: string | null, options?: SaveOptions) => {
      if (saveTimers.current[path]) {
        clearTimeout(saveTimers.current[path]!);
      }
      saveTimers.current[path] = setTimeout(() => {
        const shouldPersistEmpty = options?.persistEmpty;
        const payload =
          code === null || (typeof code === 'string' && code.trim().length === 0 && !shouldPersistEmpty)
            ? null
            : code;
        setEdgeLiquid(edgeId, path, payload);
        delete saveTimers.current[path];
      }, 600);
    },
    [edgeId, setEdgeLiquid],
  );

  const handleDraftChange = useCallback(
    (path: string, value: string) => {
      setDrafts((prev) => ({ ...prev, [path]: value }));
      scheduleSave(path, value, { persistEmpty: value.trim().length > 0 });
    },
    [scheduleSave],
  );

  const handleRemove = useCallback(
    (path: string) => {
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[path];
        return next;
      });
      if (saveTimers.current[path]) {
        clearTimeout(saveTimers.current[path]!);
        delete saveTimers.current[path];
      }
      setEdgeLiquid(edgeId, path, null);
      // Remove from user-added tracking
      setUserAddedFields((prev) => {
        const next = new Set(prev);
        next.delete(path);
        return next;
      });
    },
    [edgeId, setEdgeLiquid],
  );

  const handleAdd = useCallback(
    (pathToAdd: string) => {
      const clean = pathToAdd.trim();
      console.log('[LiquidGrid] handleAdd called with:', clean);
      if (!clean) return;
      if (drafts[clean]) {
        console.log('[LiquidGrid] Field already exists, skipping:', clean);
        return;
      }
      setDrafts((prev) => ({ ...prev, [clean]: '' }));
      scheduleSave(clean, '', { persistEmpty: true });
      // Track this field as user-added
      setUserAddedFields((prev) => {
        const next = new Set(prev).add(clean);
        console.log('[LiquidGrid] Updated userAddedFields:', Array.from(next));
        return next;
      });
    },
    [drafts, scheduleSave],
  );

  const sortedTargets = useMemo(() => Object.keys(drafts).sort((a, b) => a.localeCompare(b)), [drafts]);

  const handleInstructionsChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInstructionsDraft(e.target.value);
  }, []);

  const handleInstructionsBlur = useCallback(() => {
    if (setEdgeInstructions) {
      setEdgeInstructions(edgeId, instructionsDraft);
    }
  }, [edgeId, instructionsDraft, setEdgeInstructions]);

  const handleRunLLMDirect = useCallback(() => {
    if (mappingLLMRunning) return;
    // Only send user-added fields that still need mapping
    const fieldsToResolve = Array.from(userAddedFields);
    console.log('[LiquidGrid] Fill Gaps clicked, userAddedFields:', fieldsToResolve);
    console.log('[LiquidGrid] Current drafts:', drafts);
    runLLM(edgeId, fieldsToResolve);
  }, [mappingLLMRunning, edgeId, runLLM, userAddedFields, drafts]);

  const handleRunLLMWithInstructions = useCallback(() => {
    if (mappingLLMRunning) return;
    if (setEdgeInstructions && instructionsDraft.trim() !== instructions) {
      setEdgeInstructions(edgeId, instructionsDraft);
    }
    // Only send user-added fields that still need mapping
    const fieldsToResolve = Array.from(userAddedFields);
    console.log('[LiquidGrid] Fill Gaps with instructions clicked, userAddedFields:', fieldsToResolve);
    runLLM(edgeId, fieldsToResolve);
    setShowInstructions(false);
  }, [mappingLLMRunning, setEdgeInstructions, instructionsDraft, instructions, edgeId, runLLM, userAddedFields]);

  const context = useMemo(() => {
    if (!edge) return null;
    return resolveEdgeSchemas(edge as any, nodes, templates);
  }, [edge, nodes, templates]);

  const targetSuggestions = useMemo(() => {
    const values = new Set<string>();
    const alreadyMapped = new Set(Object.keys(drafts));

    const targetPins = (context?.targetNode?.data as any)?.pins?.data?.in ?? [];
    if (Array.isArray(targetPins)) {
      for (const pin of targetPins) {
        if (pin?.id) values.add(String(pin.id));
      }
    }

    const schemaCandidate = context?.targetSchema ?? context?.targetInputSchema;
    if (schemaCandidate && typeof schemaCandidate === 'object') {
      try {
        const fields = flattenProperties(schemaCandidate as any, { maxFields: 256 });
        for (const field of fields) {
          if (field?.path) {
            values.add(field.path);
          }
        }
      } catch {
        /* ignore schema flatten errors */
      }
    }

    // Filter out already-mapped fields
    return Array.from(values).filter((v) => Boolean(v) && !alreadyMapped.has(v));
  }, [context, drafts]);


  const handleRenameField = useCallback(
    (oldName: string, newName: string) => {
      const trimmed = newName.trim();
      if (!trimmed || trimmed === oldName) {
        setEditingField(null);
        return;
      }
      if (drafts[trimmed]) {
        // Field already exists, cancel
        setEditingField(null);
        return;
      }

      // Move the value to the new key
      const value = drafts[oldName];
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[oldName];
        next[trimmed] = value;
        return next;
      });

      // Clear old save timer
      if (saveTimers.current[oldName]) {
        clearTimeout(saveTimers.current[oldName]!);
        delete saveTimers.current[oldName];
      }

      // Update user-added tracking if the old field was tracked
      setUserAddedFields((prev) => {
        if (prev.has(oldName)) {
          const next = new Set(prev);
          next.delete(oldName);
          next.add(trimmed);
          return next;
        }
        return prev;
      });

      // Remove old field and save new one
      setEdgeLiquid(edgeId, oldName, null);
      const shouldPersistEmpty = typeof value === 'string' && value.trim().length === 0;
      scheduleSave(trimmed, value || '', { persistEmpty: shouldPersistEmpty });
      setEditingField(null);
    },
    [drafts, edgeId, scheduleSave, setEdgeLiquid],
  );



  if (!edge) {
    return <div className="text-sm text-muted-foreground">Edge not found.</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <TargetFieldCombobox suggestions={targetSuggestions} onAddField={handleAdd} />
        <ButtonGroup>
          <Button
            size="sm"
            variant="outline"
            onClick={handleRunLLMDirect}
            disabled={mappingLLMRunning}
          >
            {mappingLLMRunning ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                Synthesizing…
              </>
            ) : (
              'Fill gaps'
            )}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowInstructions(!showInstructions)}
            className="px-2"
          >
            {showInstructions ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            {instructions && !showInstructions && <span className="ml-1 text-[10px] text-muted-foreground">✓</span>}
          </Button>
        </ButtonGroup>
        <Badge variant="secondary" className="ml-auto">
          {sortedTargets.length} mapped field{sortedTargets.length === 1 ? '' : 's'}
        </Badge>
      </div>

      {showInstructions && (
        <div className="space-y-2 rounded-md border bg-muted/30 p-3">
          <Label htmlFor="mapping-instructions" className="text-xs font-medium">
            Optional Instructions for LLM
          </Label>
          <Textarea
            id="mapping-instructions"
            placeholder="e.g., 'Convert all date fields to ISO format' or 'Map currency codes to symbols'..."
            value={instructionsDraft}
            onChange={handleInstructionsChange}
            onBlur={handleInstructionsBlur}
            className="min-h-[100px] resize-y text-xs"
          />
          <Button
            onClick={handleRunLLMWithInstructions}
            disabled={mappingLLMRunning}
            className="w-full"
            size="sm"
          >
            {mappingLLMRunning ? (
              <>
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                Synthesizing…
              </>
            ) : (
              'Send'
            )}
          </Button>
        </div>
      )}

      {mappingLLMError && !mappingLLMRunning && (
        <div className="rounded-md border border-destructive/60 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {mappingLLMError}
        </div>
      )}

      {sortedTargets.length === 0 ? (
        <div className="rounded-md border border-dashed bg-muted/20 px-3 py-6 text-center text-sm text-muted-foreground">
          No Liquid mappings yet. Add a target field to start authoring.
        </div>
      ) : (
        <div className="space-y-2">
          {sortedTargets.map((target) => {
            const value = drafts[target] ?? '';
            const committed = mappingLiquid[target] ?? '';
            const isDirty = value !== committed;
            const isEditing = editingField === target;
            return (
              <div key={target} className="flex items-center gap-2 rounded-md border px-2 py-1.5">
                {isEditing ? (
                  <input
                    autoFocus
                    type="text"
                    value={fieldNameDraft}
                    onChange={(e) => setFieldNameDraft(e.target.value)}
                    onBlur={() => handleRenameField(target, fieldNameDraft)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleRenameField(target, fieldNameDraft);
                      } else if (e.key === 'Escape') {
                        setEditingField(null);
                      }
                    }}
                    className="font-mono text-xs w-[320px] flex-shrink-0 px-2 py-1 border rounded bg-background"
                  />
                ) : (
                  <button
                    onClick={() => {
                      setEditingField(target);
                      setFieldNameDraft(target);
                    }}
                    className="font-mono text-xs text-muted-foreground hover:text-foreground w-[320px] flex-shrink-0 text-left px-2 py-1 rounded hover:bg-muted/50"
                  >
                    {target}
                  </button>
                )}
                <div className="flex-1 min-w-0">
                  <CodeEditor
                    value={value}
                    options={{
                      language: 'liquid',
                      minHeight: 32,
                      maxHeight: 200,
                      sourceSchema: context?.sourceSchema,
                    }}
                    onChange={(next) => handleDraftChange(target, next ?? '')}
                    className={cn(isDirty ? 'ring-1 ring-muted-foreground/40 rounded' : undefined)}
                  />
                </div>
                {isDirty && <Badge variant="outline" className="text-[10px] h-5">unsaved</Badge>}
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6 flex-shrink-0 text-muted-foreground hover:text-destructive"
                  onClick={() => handleRemove(target)}
                >
                  <Trash2 className="h-3 w-3" />
                  <span className="sr-only">Remove mapping</span>
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
